/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useEffect, useRef } from 'react';
import { s3Url } from '@/utils/common/env.constants';
import { IcertificateDetails } from '../../TypesAndInterfaces';
import {
  sendChooseCertificatePopupShownEvent,
  sendChooseCertificateConfirmClickedEvent,
} from '../utils/certificateGAEvents';
import CertificateSelectionModal from './CertificateSelectionModal';

interface LinkedInShareModalProps {
  isOpen: boolean;
  onClose: () => void;
  allCertificates: IcertificateDetails[];
  selectedIndex: number;
  onShare: (shareUrl: string, selectedCertIds: string[]) => void;
}

const LinkedInShareModal: React.FC<LinkedInShareModalProps> = ({
  isOpen,
  onClose,
  allCertificates,
  selectedIndex,
  onShare,
}) => {
  const popupShownForCurrentOpen = useRef(false);
  const partnerCertCount = allCertificates.filter((c) => c.isPartnerCertificate).length;
  const currentCert = allCertificates[selectedIndex];

  // Reset flag when modal closes
  useEffect(() => {
    if (!isOpen) {
      popupShownForCurrentOpen.current = false;
    }
  }, [isOpen]);

  // Track popup shown event (only once per modal open)
  useEffect(() => {
    if (isOpen && allCertificates.length > 0 && !popupShownForCurrentOpen.current) {
      popupShownForCurrentOpen.current = true;
      sendChooseCertificatePopupShownEvent({
        popup_type: 'share',
        certificates_count: allCertificates.length,
        certificate_ids: allCertificates.map((c) => c.certificate?.id).filter(Boolean),
        course_name: currentCert?.courseName,
        course_id: currentCert?.certificate?._id,
        partner_certs_available_count: partnerCertCount,
      });
    }
  }, [isOpen, allCertificates, currentCert, partnerCertCount]);

  const handleSubmit = (selectedCertificates: IcertificateDetails[]) => {
    const selectedCertIds = selectedCertificates.map((cert) => cert.certificate?.id);

    if (selectedCertIds.length === 0) {
      return;
    }

    // Track confirm clicked event
    sendChooseCertificateConfirmClickedEvent({
      action_type: 'share_linkedin',
      certificates_count: selectedCertificates.length,
      certificate_ids: selectedCertIds,
      course_name: currentCert?.courseName,
      course_id: currentCert?.certificate?._id,
      partner_certs_available_count: partnerCertCount,
    });

    // Generate share URL based on selection
    let shareUrl: string;
    if (selectedCertIds.length === 1) {
      // Single certificate - direct PDF link
      shareUrl = `${s3Url}/learn/certificates/${selectedCertIds[0]}.pdf`;
    } else {
      // Multiple certificates - link to certificate page with query params
      const baseUrl = `${window.location.origin}/learn/certificate/${selectedCertIds[0]}`;
      const queryParams = selectedCertIds
        .slice(1)
        .map((id, index) => `id${index + 1}=${id}`)
        .join('&');
      shareUrl = queryParams ? `${baseUrl}?${queryParams}` : baseUrl;
    }

    onShare(shareUrl, selectedCertIds);
    onClose();
  };

  return (
    <CertificateSelectionModal
      isOpen={isOpen}
      onClose={onClose}
      allCertificates={allCertificates}
      selectedIndex={selectedIndex}
      title="Choose Certificates"
      description="Showcase your achievements! Choose which certificates you would like to display on your LinkedIn profile."
      submitButtonText="Confirm"
      onSubmit={handleSubmit}
      showSelectAll
      sharedMedium="linkedin"
    />
  );
};

export default LinkedInShareModal;
